import type { DebandadeEffect } from '../../../data/full/poussin';
import { hurtEnemy } from '../../effects';
import { applyEnemyStatus } from '../../enemy-status';
import type { SkillModule } from '../../skills/types';
import type { PlayerState } from '../../state';
import type { StepContext } from '../../systems/types';
import { CHARGING_CHICK, CHICK, attackOf, bandSize, keepBand } from '../attacks/brood';
import { ownerOf } from '../../props';

export const debandade: SkillModule<DebandadeEffect> = {
  cast(ctx, player, effect, _input, power) {
    const { state } = ctx;
    const attack = attackOf(ctx, player);
    const band = keepBand(state, player, attack, bandSize(player, attack)).sort(
      (a, b) => (a.rank ?? 0) - (b.rank ?? 0),
    );
    const across = { x: -player.aim.y, y: player.aim.x };
    band.forEach((chick, index) => {
      const offset = (index - (band.length - 1) / 2) * effect.spacing;
      chick.kind = CHARGING_CHICK;
      chick.phase = 'flying';
      chick.x = player.x + across.x * offset;
      chick.y = player.y + across.y * offset;
      chick.vx = player.aim.x * effect.speed;
      chick.vy = player.aim.y * effect.speed;
      chick.ticksLeft = Math.ceil(effect.length / effect.speed);
      chick.damage = effect.damage * power;
      chick.hitIds = [];
      delete chick.targetId;
    });
  },

  update(ctx) {
    for (const chick of ctx.state.props ?? []) {
      if (chick.kind !== CHARGING_CHICK) {
        continue;
      }
      const owner = ownerOf(ctx.state, chick);
      const effect = debandadeOf(ctx, owner);
      const from = { x: chick.x, y: chick.y };
      chick.x += chick.vx;
      chick.y += chick.vy;
      for (const enemy of ctx.state.enemies) {
        const reach = effect.radius + enemy.radius;
        const dx = enemy.x - chick.x;
        const dy = enemy.y - chick.y;
        if (
          enemy.hp <= 0 ||
          dx * dx + dy * dy > reach * reach ||
          chick.hitIds?.includes(enemy.id)
        ) {
          continue;
        }
        (chick.hitIds ??= []).push(enemy.id);
        hurtEnemy(ctx.state, enemy, chick.damage, owner.id, from);
        applyEnemyStatus(ctx.state, enemy, 'frozen', effect.knockBeats * ctx.tempo.ticksPerBeat);
      }
      chick.ticksLeft -= 1;
      if (chick.ticksLeft <= 0) {
        chick.kind = CHICK;
        chick.phase = 'returning';
        chick.vx = chick.vy = 0;
        delete chick.hitIds;
      }
    }
  },
};

function debandadeOf(ctx: Pick<StepContext, 'content'>, owner: PlayerState): DebandadeEffect {
  const effect = ctx.content.classes.get(owner.classId)?.skill.effect;
  if (effect?.kind !== 'debandade') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" has no débandade`);
  }
  return effect;
}
